import React, { useState, useMemo } from 'react';
import { PlusCircle } from '@piiqtechnologies/svg-icons';
import { resolvePath } from './PeekInput.js';
import { PeekButton } from './PeekButton.js';
import { PeekChip } from './PeekChip.js';
import { PeekFormControl } from './PeekFormControl.js';
import { PeekFormLabel } from './PeekFormLabel.js';
import { PeekGrid } from './PeekGrid.js';
import { PeekListItemText } from './PeekListItemText.js';
import { PeekMenuItem } from './PeekMenuItem.js';
import { PeekSelect } from './PeekSelect.js';
import { PeekStack } from './PeekStack.js';
import { PeekTypography } from './PeekTypography.js';
import { useTheme } from '@mui/material/styles';

const SELECT_ALL_OPTION = "all";
const CLEAR_SELECTION_OPTION = "clear";
const PeekSelectedValue = ({ selected, placeholder, options, removeSelected, handleNewOption, toggleSelectOptions, sx, }) => {
    const getLabel = (options, optionId) => {
        var _a;
        return (_a = options.find((option) => option._id === optionId)) === null || _a === void 0 ? void 0 : _a.name;
    };
    const AddNewOption = () => {
        return (React.createElement(PeekButton, { sx: { minWidth: 0, height: 25 }, startIcon: React.createElement(PlusCircle, null), onClick: handleNewOption },
            React.createElement(PeekTypography, { variant: "body1", color: "text.disabled" }, "Add New")));
    };
    if (!(selected === null || selected === void 0 ? void 0 : selected.length)) {
        return (React.createElement(PeekStack, { direction: "row", spacing: 0.5, alignItems: "center" },
            React.createElement(PeekTypography, { variant: "body1", color: "text.disabled", onClick: toggleSelectOptions }, placeholder),
            handleNewOption && AddNewOption()));
    }
    return (React.createElement(PeekGrid, { container: true, spacing: 0.5, alignItems: "center" }, selected === null || selected === void 0 ? void 0 :
        selected.map((value, index) => (React.createElement(PeekGrid, { item: true, key: index },
            React.createElement(PeekChip, { color: "primary", size: "small", clickable: true, label: getLabel(options, value), onDelete: () => {
                    removeSelected(value);
                }, sx: sx })))),
        React.createElement(PeekGrid, { item: true, key: "default" },
            React.createElement(PeekTypography, { variant: "body1", color: "text.disabled", lineHeight: "24px", onClick: toggleSelectOptions }, placeholder)),
        React.createElement(PeekGrid, { item: true, key: "default" }, handleNewOption && AddNewOption())));
};
const PeekMultipleSelector = ({ form, field, label, placeholder, options, isRequired, selectedOptions, handleNewOption, onSelectionChange, disabled, ValueChipSxProps, }) => {
    const theme = useTheme();
    const [openSelectOptions, setOpenSelectOptions] = useState(false);
    const { name } = field;
    const fieldTouched = resolvePath(form === null || form === void 0 ? void 0 : form.touched, name);
    const fieldError = resolvePath(form === null || form === void 0 ? void 0 : form.errors, name);
    const error = fieldTouched && Boolean(fieldError);
    const errorText = fieldTouched && fieldError;
    const toggleSelectOptions = () => {
        setOpenSelectOptions(!openSelectOptions);
    };
    const handleSelectAssociatedOption = (event) => {
        if (event.target.value.includes(SELECT_ALL_OPTION)) {
            const optionsSelected = event.target.value.includes(SELECT_ALL_OPTION)
                ? options.map((prop) => prop._id)
                : event.target.value;
            onSelectionChange(optionsSelected);
        }
        else if (event.target.value.includes(CLEAR_SELECTION_OPTION)) {
            onSelectionChange([]);
        }
        else {
            onSelectionChange(event.target.value);
        }
    };
    const removeSelectedAssociatedOption = (AssociatedOptionsId) => {
        onSelectionChange(selectedOptions.filter((propId) => propId !== AssociatedOptionsId));
    };
    const SelectUnselectAllOption = useMemo(() => {
        const isAllSelected = (options === null || options === void 0 ? void 0 : options.length) === (selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.length);
        return (React.createElement(PeekMenuItem, { hoverBgColor: "info", selectedBgColor: "info", key: isAllSelected ? CLEAR_SELECTION_OPTION : SELECT_ALL_OPTION, value: isAllSelected ? CLEAR_SELECTION_OPTION : SELECT_ALL_OPTION, "aria-label": isAllSelected ? "Clear Selection" : "Select All" },
            React.createElement(PeekListItemText, null, isAllSelected ? "Clear Selection" : "Select All")));
    }, [options, selectedOptions]);
    const handleRequiredLabel = () => {
        return error ? (React.createElement(PeekTypography, { color: theme.palette.error.main },
            label,
            " ",
            React.createElement("span", null, "*"))) : (React.createElement(PeekTypography, null,
            label,
            " ",
            React.createElement("span", { style: { color: `${theme.palette.error.main}` } }, "*")));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekFormControl, { fullWidth: true },
            React.createElement(PeekFormLabel, null, isRequired ? handleRequiredLabel() : label),
            React.createElement(PeekSelect, { multiple: true, showBorder: true, key: "_id", displayEmpty: true, disabled: disabled, error: error, open: openSelectOptions, IconComponent: () => null, onClose: toggleSelectOptions, onChange: handleSelectAssociatedOption, value: selectedOptions ? selectedOptions : [], sx: { minWidth: "100%", minHeight: 35, backgroundColor: "common.white" }, MenuProps: {
                    disablePortal: true,
                }, renderValue: (selected) => {
                    return (React.createElement(PeekSelectedValue, { options: options, selected: selected, placeholder: placeholder || label, handleNewOption: handleNewOption, toggleSelectOptions: toggleSelectOptions, removeSelected: removeSelectedAssociatedOption, sx: ValueChipSxProps }));
                } },
                SelectUnselectAllOption, options === null || options === void 0 ? void 0 :
                options.map((option) => {
                    return (React.createElement(PeekMenuItem, { key: option._id, value: option._id, hoverBgColor: "info", selectedBgColor: "info", "aria-label": option.name },
                        React.createElement(PeekListItemText, { primaryTypographyProps: {
                                fontWeight: (selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.includes(option._id)) ? "fontWeightBold" : "fontWeightRegular",
                            } }, option.name)));
                }))),
        !!errorText && (React.createElement(PeekTypography, { variant: "body1", color: "error" }, errorText))));
};

export { PeekMultipleSelector, PeekMultipleSelector as default };
